/**
 * Query expansion for site search.
 *
 * People search the way they talk ("crockpot", "airfryer", "sweets") and they
 * misspell things constantly. The database side handles typos with trigram
 * similarity; this file handles vocabulary — mapping what people type to the
 * words that actually appear in recipe titles, ingredients and blurbs.
 */

const SYNONYMS: Record<string, string[]> = {
  // Equipment / method
  crockpot: ["slow cooker"],
  "crock pot": ["slow cooker"],
  slowcooker: ["slow cooker"],
  airfryer: ["air fryer"],
  "air-fryer": ["air fryer"],
  instapot: ["instant pot", "pressure cooker"],
  "insta pot": ["instant pot"],
  "instant pot": ["pressure cooker"],
  sheetpan: ["sheet pan", "one pan"],
  "sheet pan": ["one pan", "baked"],
  "one pot": ["one pan", "skillet"],
  skillet: ["cast iron", "pan"],
  grill: ["grilled", "bbq", "barbecue"],
  bbq: ["barbecue", "grilled"],
  oven: ["baked", "bake"],

  // Proteins
  hamburger: ["ground beef"],
  mince: ["ground beef"],
  "ground meat": ["ground beef", "ground turkey"],
  steak: ["beef"],
  bacon: ["pork"],
  shrimp: ["seafood"],
  fish: ["seafood", "salmon"],

  // Courses / cravings
  sweets: ["dessert", "cake", "cookie"],
  sweet: ["dessert"],
  treats: ["dessert", "snack"],
  pudding: ["dessert"],
  supper: ["dinner"],
  entree: ["dinner"],
  "main dish": ["dinner"],
  appetizer: ["snack", "appetizers", "party"],
  apps: ["appetizer", "snack"],
  brunch: ["breakfast"],
  lunch: ["dinner", "salad", "sandwich"],
  side: ["sides", "side dish"],
  drink: ["drinks", "cocktail", "beverage"],

  // Occasions
  thanksgiving: ["holiday", "turkey"],
  christmas: ["holiday"],
  xmas: ["holiday", "christmas"],
  "4th of july": ["fourth of july", "holiday", "summer"],
  july4: ["fourth of july", "holiday"],
  halloween: ["holiday", "fall"],
  easter: ["holiday", "spring"],
  potluck: ["party", "crowd"],
  tailgate: ["party", "game day"],

  // Diet
  glutenfree: ["gluten free"],
  "gluten-free": ["gluten free"],
  lowcarb: ["low carb", "keto"],
  keto: ["low carb"],
  vegetarian: ["meatless", "veggie"],
  veggie: ["vegetarian", "vegetables"],
  healthy: ["light", "salad"],

  // Speed / effort
  quick: ["easy", "fast", "30 minute"],
  fast: ["quick", "easy"],
  easy: ["simple", "quick"],
  "30 min": ["30 minute", "quick"],
  weeknight: ["quick", "easy", "dinner"],
  budget: ["cheap", "affordable"],
  cheap: ["budget"],
  "meal prep": ["make ahead", "freezer"],
  leftovers: ["make ahead"],

  // Common misspellings that trigram alone can miss on multi-word terms
  brocoli: ["broccoli"],
  brocolli: ["broccoli"],
  chiken: ["chicken"],
  chickn: ["chicken"],
  cheeze: ["cheese"],
  potatoe: ["potato"],
  potatos: ["potatoes"],
  tomatoe: ["tomato"],
  sandwhich: ["sandwich"],
  desert: ["dessert"],
  ceasar: ["caesar"],
  quesadila: ["quesadilla"],
  jalepeno: ["jalapeno"],
  zuchini: ["zucchini"],
  casarole: ["casserole"],
  cassarole: ["casserole"],
  bruschetta: ["bruschetta", "appetizer"],
  spagetti: ["spaghetti"],
  lasagna: ["lasagne"],
  lasagne: ["lasagna"],
};

/** Singular/plural normalisation so "cookies" finds "cookie". */
function stem(word: string): string | null {
  if (word.length > 4 && word.endsWith("ies")) return word.slice(0, -3) + "y";
  if (word.length > 3 && word.endsWith("es")) return word.slice(0, -2);
  if (word.length > 3 && word.endsWith("s") && !word.endsWith("ss")) return word.slice(0, -1);
  return null;
}

/**
 * Expand a raw user query into a search string containing the original words
 * plus any known aliases, plural/singular variants and corrections.
 */
export function expandQuery(raw: string): string {
  const q = raw.trim().toLowerCase();
  if (!q) return "";

  const out = new Set<string>();
  q.split(/\s+/).filter((w) => w.length >= 2).forEach((w) => out.add(w));

  // Whole-phrase aliases first (e.g. "air fryer", "4th of july")
  for (const [key, values] of Object.entries(SYNONYMS)) {
    if (key.includes(" ") && q.includes(key)) values.forEach((v) => v.split(/\s+/).forEach((w) => out.add(w)));
  }

  for (const word of Array.from(out)) {
    const aliases = SYNONYMS[word];
    if (aliases) aliases.forEach((a) => a.split(/\s+/).forEach((w) => out.add(w)));
    const stemmed = stem(word);
    if (stemmed && stemmed.length >= 3) {
      out.add(stemmed);
      (SYNONYMS[stemmed] ?? []).forEach((a) => a.split(/\s+/).forEach((w) => out.add(w)));
    }
  }

  return Array.from(out).join(" ");
}

/** Suggestions shown before anyone types, and after a search with no results. */
export const POPULAR_SEARCHES = [
  "chicken",
  "easy weeknight dinner",
  "air fryer",
  "seasoning mix",
  "dessert",
  "breakfast",
  "slow cooker",
  "snacks",
];
